import { useCallback, useEffect, useMemo, useState } from 'react'
import { sileo } from 'sileo'
import { useApp } from '../context/AppContext'
import incidentService from '../services/incidentService'

/**
 * Nombre legible del usuario actual, usado para poblar `CreatedBy`,
 * `ReportedBy` y `ClosedBy` (columnas de texto libre en la base).
 *
 * @param {Object|null} employee - Empleado del contexto.
 * @param {Object|null} user - Usuario de Supabase Auth.
 * @returns {string}
 */
export const currentUserName = (employee, user) =>
  `${employee?.nombre || ''} ${employee?.apellido || ''}`.trim() ||
  user?.email ||
  'Desconocido'

/**
 * Hook de gestión de incidentes de seguridad (SGSI) de InventoryIT.
 * Centraliza carga, filtros, catálogo de activos y las mutaciones.
 *
 * @returns {Object} Estado y acciones sobre incidentes.
 */
const useIncidents = () => {
  const { employee, user } = useApp()

  const [incidents, setIncidents] = useState([])
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  /** Conteos por estado sobre el total de incidentes (sin filtros). */
  const [stats, setStats] = useState({
    total: 0,
    abierto: 0,
    curso: 0,
    cerrado: 0,
  })
  const [filters, setFiltersState] = useState({
    status: '',
    severity: '',
    search: '',
  })
  // Contador que fuerza la recarga aunque los filtros no cambien de valor
  // (botón "recargar", o volver a buscar el mismo término).
  const [reloadToken, setReloadToken] = useState(0)

  const author = useMemo(
    () => currentUserName(employee, user),
    [employee, user]
  )

  useEffect(() => {
    let active = true

    incidentService.getAll(filters).then(({ success, data, error }) => {
      if (!active) return
      if (!success) {
        sileo.error({ title: 'Error cargando incidentes', description: error })
        setIncidents([])
      } else {
        setIncidents(data)
      }
      setLoading(false)
    })

    // Los totales del resumen son del universo completo, no del listado
    // filtrado, para que las tarjetas sigan siendo comparables.
    incidentService.getStatusCounts().then(({ success, data }) => {
      if (active && success) setStats(data)
    })

    return () => {
      active = false
    }
  }, [filters, reloadToken])

  /**
   * Actualiza los filtros y dispara una recarga.
   * @param {Object|Function} updater - Nuevo objeto de filtros o actualizador.
   */
  const setFilters = useCallback((updater) => {
    setLoading(true)
    setFiltersState(updater)
    setReloadToken((token) => token + 1)
  }, [])

  /** Recarga la lista con los filtros activos. */
  const refresh = useCallback(() => {
    setLoading(true)
    setReloadToken((token) => token + 1)
  }, [])

  // El catálogo de activos cambia poco: se carga una sola vez.
  useEffect(() => {
    let isMounted = true
    incidentService.getAssets().then(({ success, data, error }) => {
      if (!isMounted) return
      if (!success) {
        sileo.error({ title: 'Error cargando activos', description: error })
        return
      }
      setAssets(data)
    })
    return () => {
      isMounted = false
    }
  }, [])

  /** Recalcula los totales del resumen tras una mutación. */
  const syncStats = useCallback(async () => {
    const { success, data } = await incidentService.getStatusCounts()
    if (success) setStats(data)
  }, [])

  /**
   * Registra un incidente nuevo.
   * @param {Object} payload - Campos del incidente.
   * @param {Array<number>} assetIds - Activos afectados.
   * @returns {Promise<boolean>} `true` si se guardó.
   */
  const create = useCallback(
    async (payload, assetIds) => {
      setSaving(true)
      const { success, data, error } = await incidentService.create(
        { ...payload, CreatedBy: author },
        assetIds
      )
      setSaving(false)

      if (!success) {
        sileo.error({ title: 'No se pudo registrar', description: error })
        return false
      }

      setIncidents((prev) => [data, ...prev])
      syncStats()
      sileo.success({ title: `Incidente ${data.Code} registrado` })
      return true
    },
    [author, syncStats]
  )

  /**
   * Actualiza un incidente existente.
   * @param {number} id
   * @param {Object} payload
   * @param {Array<number>} assetIds
   * @returns {Promise<boolean>}
   */
  const update = useCallback(
    async (id, payload, assetIds) => {
      setSaving(true)
      const { success, data, error } = await incidentService.update(
        id,
        payload,
        assetIds
      )
      setSaving(false)

      if (!success) {
        sileo.error({ title: 'No se pudo actualizar', description: error })
        return false
      }

      setIncidents((prev) => prev.map((item) => (item.Id === id ? data : item)))
      syncStats()
      sileo.success({ title: `Incidente ${data.Code} actualizado` })
      return true
    },
    [syncStats]
  )

  /**
   * Cambia el estado de un incidente (cerrar / reabrir / poner en curso).
   * @param {Object} incident - Incidente afectado.
   * @param {string} status - Nuevo estado.
   * @returns {Promise<boolean>}
   */
  const changeStatus = useCallback(
    async (incident, status) => {
      const action =
        status === 'Cerrado'
          ? incidentService.close(incident.Id, author)
          : status === 'En curso' && incident.Status === 'Cerrado'
            ? incidentService.reopen(incident.Id)
            : incidentService.update(incident.Id, { Status: status })

      const { success, data, error } = await action

      if (!success) {
        sileo.error({
          title: 'No se pudo cambiar el estado',
          description: error,
        })
        return false
      }

      setIncidents((prev) =>
        prev.map((item) => (item.Id === incident.Id ? data : item))
      )
      syncStats()
      sileo.success({ title: `Incidente ${data.Code}: ${status}` })
      return true
    },
    [author, syncStats]
  )

  /**
   * Elimina un incidente.
   * @param {Object} incident
   * @returns {Promise<boolean>}
   */
  const remove = useCallback(
    async (incident) => {
      const { success, error } = await incidentService.remove(incident.Id)
      if (!success) {
        sileo.error({ title: 'No se pudo eliminar', description: error })
        return false
      }
      setIncidents((prev) => prev.filter((item) => item.Id !== incident.Id))
      syncStats()
      sileo.success({ title: `Incidente ${incident.Code} eliminado` })
      return true
    },
    [syncStats]
  )

  return {
    incidents,
    assets,
    loading,
    saving,
    filters,
    setFilters,
    stats,
    author,
    refresh,
    create,
    update,
    changeStatus,
    remove,
  }
}

export default useIncidents
